import { FunctionComponent, useMemo, useState } from 'react';
import { Box, Paper, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';
import { axisClasses } from '@mui/x-charts';
import { applyTopNToStackedSeries, IStackedSeriesItem, TopNValue } from '../../../../shared/utils/analytics-utils';
import { TopNSelect } from './top-n-select';

const LEGEND_LIMIT = 10;

export const GraphBarChartStacked: FunctionComponent<{
  backgroundColor?: string,
  title: string,
  textColor?: string,
  tooltip?: string,
  colors: string[],
  dataset: Array<{}>, series: Array<IStackedSeriesItem>,
  chartHeight?: number,
  paperPadding?: number,
  paperHeight?: number | string,
  xAxisDataKey?: string,
  /** Show a Top N dropdown that folds the smallest SERIES into "Other" client-side. */
  enableTopN?: boolean,
  defaultTopN?: TopNValue,
  /** Hide the title/tooltip header row (e.g. when the caller renders its own fixed title). */
  hideHeader?: boolean,
  /**
   * Hide the per-series legend swatches, for a single-series chart whose title already names
   * the series. A comparison period's swatch still renders: that one is not self-evident.
   */
  hideSeriesLegend?: boolean,
  /**
   * A comparison period's per-row totals (aligned by row index, i.e. month 1 to month 1),
   * rendered as a neutral grey reference bar beside each stack.
   */
  comparison?: { totals: number[], label: string, valueFormatter?: (value: any) => any },
  /**
   * Click-to-drill: fires with the clicked segment's SERIES key (the entity label, i.e. the
   * series dataKey) and the x-axis (month) index. The comparison-total and "Other" series are
   * never reported. The caller resolves the label to an entity id.
   */
  onSelect?: (seriesKey: string, dataIndex: number) => void,
}> = ({
  title, tooltip, dataset, series, backgroundColor = 'fff', textColor = 'text.primary', colors, chartHeight = 400, paperPadding = 4, paperHeight = '100%', xAxisDataKey = 'month',
  enableTopN = false,
  defaultTopN = 10,
  hideHeader = false,
  hideSeriesLegend = false,
  comparison,
  onSelect }) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
    // When a caller pins a fixed pixel `paperHeight` (only the PDF-export report
    // cards do this, to height-match a sibling widget), keep the legend + chart
    // centred inside a chartHeight-tall box so the card renders at exactly that
    // height. On screen (paperHeight '100%') let the card grow to its content so
    // the legend sits cleanly above the chart instead of overlapping it, matching
    // GraphDonutChart.
    const isFixedHeight = typeof paperHeight === 'number';

    const [topN, setTopN] = useState<TopNValue>(defaultTopN);
    const { dataset: displayedDataset, series: displayedSeries } = useMemo(
      () => (enableTopN
        ? applyTopNToStackedSeries(dataset, series, topN)
        : { dataset, series }),
      [enableTopN, dataset, series, topN],
    );
    // The comparison is a per-row TOTAL (not entity-sliced), so it ignores Top N and renders
    // as its own grey bar beside each stack, in its own stack group.
    const comparisonColor = gaiaTheme.palette.grey[500];
    const chartDataset = useMemo(() => (
      comparison
        ? displayedDataset.map((row, index) => ({ ...row, __comparisonTotal__: comparison.totals[index] ?? 0 }))
        : displayedDataset
    ), [displayedDataset, comparison]);
    const chartSeries = useMemo(() => {
      const withComparison = comparison
        ? [...displayedSeries, {
          dataKey: '__comparisonTotal__',
          id: '__comparisonTotal__',
          label: `${comparison.label} total`,
          stack: 'comparison',
          color: comparisonColor,
          valueFormatter: comparison.valueFormatter,
        } as IStackedSeriesItem]
        : displayedSeries;
      // MUI reports the clicked series by its `id`; the series dataKey is the entity label, so
      // give every series an explicit id (falling back to the dataKey) or onItemClick returns an
      // auto-generated id we cannot resolve back to an entity.
      return withComparison.map((s) => ({ ...s, id: s.id ?? s.dataKey }));
    }, [displayedSeries, comparison, comparisonColor]);
    // Only "All" can outrun the legend budget (every other Top N collapses the
    // tail), but 50+ categories at 180px each would blow the card out, so the
    // overflow goes into the donut's "+N more" tooltip. Non-Top-N callers keep
    // the flat legend.
    const legendSeries = hideSeriesLegend ? [] : displayedSeries;
    const legendVisible = enableTopN ? legendSeries.slice(0, LEGEND_LIMIT) : legendSeries;
    const legendHidden = enableTopN ? legendSeries.slice(LEGEND_LIMIT) : [];
    // With no swatches to draw, the legend row would still take its margins and push the chart
    // down, so drop the row entirely rather than render an empty one.
    const hasLegendRow = legendVisible.length > 0 || legendHidden.length > 0 || Boolean(comparison);
    // MUI cycles the palette (colors[i % len]) but getMixedColorPalette only
    // returns 6, so an unmoduloed swatch goes transparent from the 7th series
    // and stops matching its own bars.
    const colorAt = (index: number) => colors[index % colors.length];

    return (
      <Paper variant='outlined' sx={{ p: paperPadding, height: paperHeight, backgroundColor: backgroundColor, border: backgroundColor === 'primary.dark' ? 'none' : '' }}>
        {!hideHeader && <Stack direction='row' alignItems={'center'}>
          <Typography variant='subtitle1' sx={{ flex: 1, color: textColor }}>
            <b>{title}</b>
          </Typography>
          {tooltip && !isMobileMode && <TooltipButton
            text={tooltip}
            noPadding={true}
            placement='right' />}
          {enableTopN && <Box sx={{ ml: 1 }}><TopNSelect value={topN} onChange={setTopN} /></Box>}
        </Stack>}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', ...(isFixedHeight ? { height: chartHeight, justifyContent: 'center' } : {}) }}>
          {hasLegendRow && <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', mt: 2, ...(isFixedHeight ? {} : { mb: 1 }) }}>
            {legendVisible.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', width: '180px' }}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: '5px',
                  backgroundColor: colorAt(index),
                  mr: 1
                }}></Box>
                <Typography sx={{ mr: 3, fontWeight: 400, color: textColor }}>{entry.label}</Typography>
              </Box>
            ))}
            {legendHidden.length > 0 && (
              <Tooltip
                arrow
                placement='top'
                slotProps={{ tooltip: { sx: { maxWidth: 560 } } }}
                title={
                  <Box
                    sx={{
                      maxHeight: '50vh',
                      overflowY: 'auto',
                      display: 'grid',
                      gridTemplateColumns: legendHidden.length > 12 ? '1fr 1fr' : '1fr',
                      columnGap: 1.5,
                      rowGap: 0.25,
                      p: 0.5,
                    }}
                  >
                    {legendHidden.map((entry, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', flexShrink: 0, backgroundColor: colorAt(LEGEND_LIMIT + i) }} />
                        <Typography variant='caption' noWrap>{entry.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Typography sx={{ mr: 3, color: 'primary.main', cursor: 'default', textDecoration: 'underline dotted' }}>
                  +{legendHidden.length} more
                </Typography>
              </Tooltip>
            )}
            {comparison && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '180px' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '5px', backgroundColor: comparisonColor, mr: 1 }}></Box>
                <Typography sx={{ mr: 3, fontWeight: 400, color: textColor }}>{`${comparison.label} total`}</Typography>
              </Box>
            )}
          </Box>}
          <BarChart
            dataset={chartDataset}
            xAxis={[{ scaleType: 'band', dataKey: xAxisDataKey, tickPlacement: 'middle', tickLabelPlacement: 'middle', tickSize: 0 }]}
            yAxis={[{ disableLine: true, tickSize: 0 }]}
            series={chartSeries}
            grid={{ horizontal: backgroundColor === 'primary.dark' ? false : true }}
            slotProps={{
              legend: { hidden: true }
            }}
            height={chartHeight}
            borderRadius={5}
            colors={colors}
            axisHighlight={{ x: 'none' }}
            onItemClick={onSelect
              ? ((_event: any, bar: { seriesId?: string | number; dataIndex: number }) => {
                const key = bar.seriesId == null ? '' : String(bar.seriesId);
                if (!key || key === '__comparisonTotal__' || key.startsWith('__other__') || key === 'Other') return;
                onSelect(key, bar.dataIndex);
              })
              : undefined}
            sx={(theme) => ({
              ...(onSelect ? { '& .MuiBarElement-root': { cursor: 'pointer' } } : {}),
              [`.${axisClasses.root}`]: {
                [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                  stroke: textColor,
                  strokeWidth: 1,
                },
                [`.${axisClasses.tickLabel}`]: {
                  fill: textColor,
                },
              },
            })}
          />
        </Box>
      </Paper>
    )
  }